import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MaskLine, FadeUp } from "./Reveal";
import Footer from "./Footer";

export default function CaseFile({ data }) {
    useEffect(() => {
        document.title = `${data.title} — Money Mac Media®`;
        return () => {
            document.title = "MONEY MAC MEDIA® — Look Rich. Make Something.";
        };
    }, [data.title]);

    return (
        <div className="bg-black text-white">
            <section className="px-5 pb-16 pt-28 md:px-8 md:pb-24 md:pt-40">
                <FadeUp>
                    <Link
                        to="/"
                        data-testid="case-study-back-link"
                        className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50 transition-colors duration-300 hover:text-mmm-red md:text-xs"
                    >
                        ← Money Mac Media
                    </Link>
                </FadeUp>

                <div className="mt-8 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] md:text-xs">
                    <FadeUp>
                        <span className="text-mmm-red">Case file</span> — {data.index}
                    </FadeUp>
                    <FadeUp className="hidden text-white/40 md:block">{data.client}</FadeUp>
                </div>

                <h1 className="mt-10 font-display text-[clamp(2.8rem,8.5vw,8.5rem)] uppercase leading-[0.88] md:mt-14">
                    {data.titleLines.map((line, i) => (
                        <MaskLine key={line} delay={0.05 + i * 0.1}>
                            <span className={i === data.titleLines.length - 1 ? "text-mmm-red" : ""}>
                                {line}
                            </span>
                        </MaskLine>
                    ))}
                </h1>

                <FadeUp delay={0.1} className="mt-10 md:mt-14">
                    <p className="max-w-3xl font-display text-2xl uppercase leading-tight md:text-4xl">
                        {data.lead}
                    </p>
                </FadeUp>

                {data.passages.map((p, i) => (
                    <Passage key={i} testid={`case-study-passage-${i + 1}`}>
                        {p}
                    </Passage>
                ))}
            </section>

            {data.gallery.map((group) => (
                <section key={group.label} className="px-5 pb-16 md:px-8 md:pb-24">
                    <SubLabel>{group.label}</SubLabel>
                    <div
                        className={`mt-6 grid grid-cols-1 gap-4 ${
                            group.items.length > 1 ? "md:grid-cols-2" : ""
                        }`}
                    >
                        {group.items.map((item, i) => (
                            <FadeUp key={item.src} delay={i * 0.05}>
                                <figure
                                    data-testid={item.testid}
                                    className="overflow-hidden border border-white/15 bg-black"
                                >
                                    <img
                                        src={item.src}
                                        alt={item.caption}
                                        loading="lazy"
                                        className="h-auto w-full object-contain"
                                    />
                                </figure>
                                <figcaption className="mt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 md:text-[10px]">
                                    {item.caption}
                                </figcaption>
                            </FadeUp>
                        ))}
                    </div>
                </section>
            ))}

            <section className="px-5 pb-20 md:px-8 md:pb-28">
                <SubLabel>The work</SubLabel>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 md:gap-x-10">
                    {data.contributions.map((c, i) => (
                        <FadeUp key={c} delay={i * 0.03}>
                            <div className="border-t border-white/15 py-4 font-mono text-[11px] uppercase leading-relaxed tracking-[0.14em] text-white/70 md:py-5 md:text-xs">
                                <span className="text-mmm-red">
                                    ({String(i + 1).padStart(2, "0")})
                                </span>{" "}
                                {c}
                            </div>
                        </FadeUp>
                    ))}
                </div>
                <div className="border-t border-white/15" />
            </section>

            <section className="px-5 pb-20 md:px-8 md:pb-28">
                <FadeUp>
                    <p
                        data-testid="case-study-services"
                        className="max-w-4xl font-mono text-[10px] uppercase leading-loose tracking-[0.2em] text-white/50 md:text-xs"
                    >
                        {data.services.map((s, i) => (
                            <span key={s}>
                                {s}
                                {i < data.services.length - 1 && (
                                    <span className="text-mmm-red"> · </span>
                                )}
                            </span>
                        ))}
                    </p>
                </FadeUp>
            </section>

            <section className="bg-white px-5 py-20 text-black md:px-8 md:py-28">
                <FadeUp className="font-mono text-[10px] uppercase tracking-[0.22em] md:text-xs">
                    <span className="text-mmm-red">005</span> — New Business
                </FadeUp>
                <FadeUp delay={0.1} className="mt-8 max-w-2xl text-base leading-relaxed text-black/60 md:text-lg">
                    <p>
                        Want a universe like this for your show, campaign, or strange
                        little idea? Tell us what you’re making.
                    </p>
                </FadeUp>
                <FadeUp delay={0.15} className="mt-10">
                    <a
                        data-testid="case-study-cta"
                        href="mailto:hello@moneymacmedia.com"
                        className="inline-block border-b-[6px] border-black pb-2 font-display text-[clamp(1.8rem,5vw,5rem)] uppercase leading-none transition-colors duration-300 hover:border-mmm-red hover:text-mmm-red"
                    >
                        Let’s talk business →
                    </a>
                </FadeUp>
                <FadeUp delay={0.2} className="mt-8">
                    <a
                        data-testid="case-study-email-link"
                        href="mailto:hello@moneymacmedia.com"
                        className="font-mono text-xs uppercase tracking-[0.2em] text-black/50 underline-offset-4 transition-colors duration-300 hover:text-mmm-red hover:underline md:text-sm"
                    >
                        hello@moneymacmedia.com
                    </a>
                </FadeUp>
            </section>

            <Footer />
        </div>
    );
}

function SubLabel({ children }) {
    return (
        <FadeUp className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40 md:text-xs">
            <span className="text-mmm-red">///</span> {children}
        </FadeUp>
    );
}

function Passage({ children, testid }) {
    return (
        <FadeUp className="mt-6 max-w-[62ch] text-base leading-relaxed text-white/70 md:text-lg">
            <p data-testid={testid}>{children}</p>
        </FadeUp>
    );
}
